import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import clsx from "clsx";
import { memo, useCallback } from "react";
import { ScenarioNode, ScenarioNodeType } from "types/scenario";
import { getBranchLabel, getBranchTone } from "../constants";
import { isMissingNode } from "../utils";
import { getOptionHandleId, type AddBranchNodeData, type CaptionNodeData, type ScenarioNodeData } from "../toReactFlow";
import { FlowAddMenu } from "./FlowAddMenu";
import { useFlowCanvasActions } from "./FlowCanvasContext";

type FlowNodeCardProps = {
  node: ScenarioNode;
  isStart: boolean;
  isSelected: boolean;
  branchIndex?: number;
  showHandles?: boolean;
};

export const FlowNodeCard = memo(
  ({ node, isStart, isSelected, branchIndex, showHandles }: FlowNodeCardProps) => {
    const missing = isMissingNode(node);
    const typeLabel = isStart ? `BẮT ĐẦU · ${node.type}` : node.type;
    const showOptions = node.type === ScenarioNodeType.Choice && !!node.options?.length;
    const branchTone = branchIndex == null ? undefined : getBranchTone(branchIndex);
    const className = clsx(
      "flowNode",
      `flowNode-${node.type}`,
      isSelected && !missing && "flowNodeSelected",
      missing && "flowNodeMissing",
      branchTone && `flowNodeBranch-${branchTone}`,
    );

    return (
      <div className={className}>
        <div className="flowNodeBar">
          <span className="flowNodeType">
            <i className="flowNodeDot" />
            {typeLabel}
          </span>
        </div>
        <div className="flowNodeBody">
          <div className="flowNodeKey">{node.key}</div>
          <div className="flowNodeContent">{node.content}</div>
          {showOptions ? (
            <div className="flowNodeOptions">
              {node.options?.map((option, index) => (
                <span key={`${option.position}-${index}`} className="flowNodeChipWrap">
                  <span className={clsx("flowNodeChip", `flowNodeChip-${getBranchTone(index)}`)}>
                    {getBranchLabel(index)} · {option.label}
                    {option.nextNodeKey ? ` → ${option.nextNodeKey}` : ""}
                  </span>
                  {showHandles ? (
                    <Handle
                      type="source"
                      id={getOptionHandleId(index)}
                      position={Position.Bottom}
                      isConnectable={false}
                      className="flowRfHandle flowRfHandleChip"
                    />
                  ) : null}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

FlowNodeCard.displayName = "FlowNodeCard";

type ScenarioRfNode = Node<ScenarioNodeData, "scenario" | "missing">;

export const ScenarioFlowNode = memo(({ data, selected, type }: NodeProps<ScenarioRfNode>) => {
  const isChoice = data.node.type === ScenarioNodeType.Choice;
  const missing = type === "missing" || isMissingNode(data.node);

  return (
    <div className="flowRfNode">
      <Handle type="target" position={Position.Top} id="target" isConnectable={false} className="flowRfHandle" />
      <FlowNodeCard
        node={data.node}
        isStart={data.isStart}
        isSelected={!!selected && !missing}
        branchIndex={data.branchIndex}
        showHandles
      />
      {isChoice ? null : (
        <Handle type="source" position={Position.Bottom} id="source" isConnectable={false} className="flowRfHandle" />
      )}
    </div>
  );
});

ScenarioFlowNode.displayName = "ScenarioFlowNode";

type AddBranchRfNode = Node<AddBranchNodeData, "addBranch">;

export const AddBranchFlowNode = memo(({ data }: NodeProps<AddBranchRfNode>) => {
  const { onAddBranchNode } = useFlowCanvasActions();

  const handlePick = useCallback(
    (type: ScenarioNodeType) => {
      onAddBranchNode(data.choiceKey, data.optionIndex, type);
    },
    [data.choiceKey, data.optionIndex, onAddBranchNode],
  );

  return (
    <div className="flowRfAdd nodrag nopan">
      <Handle type="target" position={Position.Top} id="target" isConnectable={false} className="flowRfHandle" />
      <FlowAddMenu
        className="flowBranchAdd"
        size="small"
        label={`Thêm vào nhánh ${data.letter}`}
        onPick={handlePick}
      />
    </div>
  );
});

AddBranchFlowNode.displayName = "AddBranchFlowNode";

type CaptionRfNode = Node<CaptionNodeData, "caption">;

export const CaptionFlowNode = memo(({ data }: NodeProps<CaptionRfNode>) => (
  <div className="flowOrphanTitle">{data.label}</div>
));

CaptionFlowNode.displayName = "CaptionFlowNode";
